import { CollectionConst } from "constants/document/CollectionConst";
import { EMPTY_REFUND_OPERATION_CRITERIA } from "domain/entity/Refund/RefundOperationCriteria";
import { capitalize } from "lodash";
import { RefundConstant, RefundPermissionCodePolicy } from "presentation/constant/Refund/RefundConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useRefundVM } from "presentation/hook/Refund/useRefundVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useRefundTracked } from "presentation/store/Refund/RefundProvider";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { memo, useCallback, useState } from "react";
import { HPHButton, IconButton, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const RefundTitleBar: React.FC = () => {
    const SCREEN_CONSTANT = RefundConstant;
    const [refundState] = useRefundTracked();
    const refundVM = useRefundVM();
    const { selectedRows, refundList, searchCriteria, isShowOperationPanel } = refundState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { allowCreate, allowUpdate } = anaInfoState;

    const handleSearchClick = useCallback(() => {
        refundVM.onSearchClick();
    }, [refundVM]);

    const handleAddClick = useCallback(() => {
        refundVM.onAddClick();
    }, [refundVM]);

    const handleOperationClick = useCallback(async (operation: string) => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (operation === RefundPermissionCodePolicy.REFUND_BUTTON_PROCESS) {
            if (selectedRows.some(entity => entity.status !== CollectionConst.refundStatus.ITEM_APPROVED)) {
                messageBarVM.showError("Cannot " + operation + " because the refund claim(s) is not in "
                    + CollectionConst.refundStatus.ITEM_APPROVED + " status.");
                return;
            }
            refundVM.onShowOperationPanel(operation);
            return;
        }
        if (operation === RefundPermissionCodePolicy.REFUND_BUTTON_REJECT) {
            if (selectedRows.some(entity => entity.status !== CollectionConst.refundStatus.ITEM_APPROVED
                && entity.status !== CollectionConst.refundStatus.ITEM_VERIFIED)) {
                messageBarVM.showError("Cannot " + operation + " because the refund claim(s) is not in "
                    + CollectionConst.refundStatus.ITEM_VERIFIED + "/" + CollectionConst.refundStatus.ITEM_APPROVED
                    + " status.");
                return;
            }
            refundVM.onShowOperationPanel(operation);
            return;
        }
        let criteria = { ...EMPTY_REFUND_OPERATION_CRITERIA };
        criteria = {
            ...criteria,
            operation: operation,
            selectRows: selectedRows,
            allRows: refundList
        }
        setIsLoading(true);
        await refundVM.onOperation(criteria).then(async data => {
            if (!data) {
                await refundVM.onSearch(searchCriteria).then(data => {
                    setIsLoading(false);
                    messageBarVM.showSuccess(capitalize(operation) + " Successfully");
                });
            } else {
                messageBarVM.showError(data);
                setIsLoading(false);
            }
        });
    }, [messageBarVM, refundList, refundVM, searchCriteria, selectedRows]);

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        <HeaderTitle>{SCREEN_CONSTANT.TITLE}</HeaderTitle>
        {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        <StyledAction className="tm-animated-wrapper">
            <IconButton fileName='Icon-search' disabled={isShowOperationPanel} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearchClick} />
            <div className="add-seperator" />
            {allowCreate && <IconButton fileName='Icon-add' disabled={isShowOperationPanel} size='medium' toolTipText={'Add'} toolTipArrow={false} onClick={handleAddClick} />}
            {allowUpdate && <>
                <HPHButton disabled={selectedRows.length === 0 || isShowOperationPanel} label={"Verify"} size={'Small'} theme={'Secondary'} onClick={() => handleOperationClick(RefundPermissionCodePolicy.REFUND_BUTTON_VERIFY)} />
                <HPHButton disabled={selectedRows.length === 0 || isShowOperationPanel} label={"Approve"} size={'Small'} theme={'Secondary'} onClick={() => handleOperationClick(RefundPermissionCodePolicy.REFUND_BUTTON_APPROVE)} />
                <HPHButton disabled={selectedRows.length === 0 || isShowOperationPanel} label={WorkspaceConstant.Common.BUTTON_REJECT} size={'Small'} theme={'Secondary'} onClick={() => handleOperationClick(RefundPermissionCodePolicy.REFUND_BUTTON_REJECT)} />
                <HPHButton disabled={selectedRows.length === 0 || isShowOperationPanel} label={"Process"} size={'Small'} theme={'Secondary'} onClick={() => handleOperationClick(RefundPermissionCodePolicy.REFUND_BUTTON_PROCESS)} />
                <HPHButton disabled={selectedRows.length === 0 || isShowOperationPanel} label={"Issue"} size={'Small'} theme={'Primary'} onClick={() => handleOperationClick(RefundPermissionCodePolicy.REFUND_BUTTON_ISSUE)} />
            </>}
        </StyledAction>
        {refundState.forceRefresh && <></>}
    </Sidebarheader >
}

export default memo(RefundTitleBar);